import React, { useState } from "react";
import styles from "./Payslip.module.scss";
import { useViewEmployeePaySlipDetails } from "../../../queries/payrollQuries";
import { toast } from "react-toastify";

import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import {
  PAY_SLIP_COMPONENT_TYPE,
  SALARY_COMPONENT_TYPES,
  PAYROLL_STATUS,
} from "../../../redux/constants/payrollComponents";
import { DatePicker } from "antd";
import dayjs from "dayjs";

const formatAmount = (val) =>
  `₹ ${Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const getTotal = (list = []) =>
  list.reduce((sum, item) => sum + parseFloat(item.calculated_value || 0), 0);

const getMonthStart = (date) =>
  dayjs(date).startOf("month").format("DD/MM/YYYY");

const getMonthEnd = (date) => dayjs(date).endOf("month").format("DD/MM/YYYY");

const Payslip = () => {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const month = selectedDate.month() + 1;
  const year = selectedDate.year();
  const status = PAYROLL_STATUS.PUBLISHED;

  const { data } = useViewEmployeePaySlipDetails({ month, year, status });
  const axiosPrivate = useAxiosPrivate();

  const payslipData = data?.data || [];
  const employeeInfo = payslipData[0];

  const earnings = payslipData.filter(
    (item) =>
      Number(item.component_type) === Number(SALARY_COMPONENT_TYPES.EARNING)
  );

  const deductions = payslipData.filter(
    (item) =>
      Number(item.component_type) === Number(SALARY_COMPONENT_TYPES.DEDUCTION)
  );

  const reimbursements = payslipData.filter(
    (item) => item.type_name === "Reimbursement"
  );

  const netSalaryItem = payslipData.find(
    (item) => item.component_name === PAY_SLIP_COMPONENT_TYPE.NET_PAY
  );
  const netSalary = netSalaryItem?.calculated_value || "0.00";
  const formattedNetSalary = formatAmount(netSalary);

  const handleDateChange = (date) => {
    if (date) {
      setSelectedDate(date);
    }
  };

 const handleDownloadPayslip = async () => {
  try {
    const response = await axiosPrivate.get("/payroll/generateEmployeePayslip", {
      params: { employee_id: employeeInfo.employee_id, month: month, year: year },
      responseType: "blob",
    });

    if (response?.status === 200) {
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;

      const formattedMonth = dayjs(`${year}-${month}-01`).format("MMMM");
      const employeeName = `${employeeInfo.first_name || ""}_${employeeInfo.last_name || ""}`
        .trim()
        .replace(/\s+/g, "_");
      const filename = `Payslip ${employeeName} ${formattedMonth} ${year}.pdf`;

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Payslip downloaded successfully");
    } else {
      toast.error("Error while downloading the payslip");
    }
  } catch (error) {
    toast.error("Failed to download the payslip. Please try again later.");
  }
};


  const pay_day =
    payslipData.find(
      (comp) => comp.component_name === PAY_SLIP_COMPONENT_TYPE.ATTENDANCE
    )?.calculated_value || "-";

  return (
    <div className="bg-gray-50 flex-1 px-10 pt-8 pb-6 flex justify-center">
      <div className="w-full max-w-[1084px]">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg pb-1">Payslips</h2>
            <p>View employee monthly payslip</p>
          </div>
          <div className="flex items-center gap-4">
            <DatePicker
              picker="month"
              value={selectedDate}
              onChange={handleDateChange}
              format="MMMM YYYY"
            />

            {payslipData.length > 0 && (
              <button
                onClick={handleDownloadPayslip}
                className="btn text-sm font-medium flex items-center"
              >
                <i className="ico-download pr-3"></i>
                Download
              </button>
            )}
          </div>
        </div>

        {payslipData.length ? (
          <>
            {/* Summary */}
            <div className="bg-white shadow-md border border-gray-100 rounded-lg mb-6">
              <div className="py-4 px-6">
                <h3 className="text-base text-gray-800 font-semibold">
                  Summary
                </h3>
              </div>
              <div className="px-6 pb-6 lg:flex gap-6">
                <div className="flex-1">
                  <ul className={styles.summary}>
                    <li>
                      <p>Name</p>
                      <p>{`${employeeInfo.first_name} ${employeeInfo.last_name} #${employeeInfo.employee_serial}`}</p>
                    </li>
                    <li>
                      <p>Work Location</p>
                      <p>{employeeInfo.office_location || "-"}</p>
                    </li>
                    <li>
                      <p>Designation</p>
                      <p>{employeeInfo.role_name}</p>
                    </li>
                    <li>
                      <p>Department</p>
                      <p>{employeeInfo.department_name || "-"}</p>
                    </li>
                    <li>
                      <p>PF A/C Number</p>
                      <p>{employeeInfo.pf_account_number || "N/A"}</p>
                    </li>
                    <li>
                      <p>UAN</p>
                      <p>{employeeInfo.uan || "N/A"}</p>
                    </li>
                    <li>
                      <p>PAN</p>
                      <p>{employeeInfo.pan_number || "N/A"}</p>
                    </li>
                    <li>
                      <p>Pay Period</p>
                      <p>
                        {employeeInfo.pay_period ||
                          `${getMonthStart(selectedDate)} - ${getMonthEnd(
                            selectedDate
                          )}`}
                      </p>
                    </li>
                    <li>
                      <p>Pay Day</p>
                      <p>{pay_day || "24"}</p>
                    </li>
                    <li>
                      <p>LOP Days</p>
                      <p>{employeeInfo.lop_days || "-"}</p>
                    </li>
                  </ul>
                </div>
                <div className="bg-green-50 rounded-md px-6 py-4 flex items-center">
                  <div>
                    <p className="text-xs text-green-700">Net Pay</p>
                    <h3 className="font-bold text-green-900 text-2xl">
                      {formattedNetSalary}
                    </h3>
                  </div>
                </div>
              </div>
            </div>

            {/* Earnings */}
            <div
              className={`bg-white shadow-md border border-gray-100 rounded-lg mb-6 ${styles.pay}`}
            >
              <div className="py-4 px-6 flex justify-between">
                <h3 className="text-base text-gray-800 font-semibold">
                  Earning
                </h3>
                <span className="text-xs">Amount (in INR)</span>
              </div>
              <div className="px-6 pb-6">
                <ul>
                  {earnings

                    .filter((item) => Number(item.calculated_value) !== 0)
                    .map((item) => (
                      <li key={item.component_name}>
                        <span>{item.component_name}</span>
                        <span className="font-medium text-gray-800">
                          {formatAmount(item.calculated_value)}
                        </span>
                      </li>
                    ))}
                </ul>
              </div>
              <div className="py-3 px-6 bg-gray-100 flex justify-between">
                <span className="font-semibold text-gray-800">
                  Gross Earning
                </span>
                <span className="font-medium text-gray-800">
                  {formatAmount(
                    getTotal(
                      earnings.filter((i) => Number(i.calculated_value) !== 0)
                    )
                  )}
                </span>
              </div>
            </div>

            {/* Deductions */}
            <div
              className={`bg-white shadow-md border border-gray-100 rounded-lg mb-6 ${styles.pay}`}
            >
              <div className="py-4 px-6 flex justify-between">
                <h3 className="text-base text-gray-800 font-semibold">
                  Deductions
                </h3>
                <span className="text-xs">Amount (in INR)</span>
              </div>
              <div className="px-6 pb-6">
                <ul>
                  {deductions

                    .filter((item) => Number(item.calculated_value) !== 0)
                    .map((item) => (
                      <li key={item.component_name}>
                        <span>{item.component_name}</span>
                        <span className="font-medium text-gray-800">
                          {formatAmount(item.calculated_value)}
                        </span>
                      </li>
                    ))}
                </ul>
              </div>
              <div className="py-3 px-6 bg-gray-100 flex justify-between">
                <span className="font-semibold text-gray-800">
                  Total Deduction
                </span>
                <span className="font-medium text-gray-800">
                  -
                  {formatAmount(
                    getTotal(
                      deductions.filter((i) => Number(i.calculated_value) !== 0)
                    )
                  )}
                </span>
              </div>
            </div>
          </>
        ) : (
          <div className="bg-white p-6 mt-8 flex items-center justify-center">
            <p className="text-gray-400">No Pay slip available.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Payslip;
